/**
 * Scheduled events on the client (2026-10-29; protocol events.ts): the
 * events of the server, which ones you are interested in, and the gateway
 * events that keep them live. Its own store, apart from the data store, so
 * the feature stays one folder.
 *
 * - READY (a new session): the list is fetched once (`GET
 *   /guilds/{g}/scheduled-events`, small: the unfinished events you can see).
 * - GUILD_SCHEDULED_EVENT_CREATE / UPDATE / DELETE and USER_ADD / REMOVE
 *   change it; a finished (completed or cancelled) event leaves it.
 * - A voice channel coming into or out of view (CHANNEL_CREATE / DELETE,
 *   which the gateway sends when your roles change) fetches it again, so
 *   events in channels you now see appear and the others go.
 * - NX_GUILD_SCHEDULED_EVENT_START: an event you are interested in has
 *   started: a toast (Join or View) and, in the background, a desktop
 *   notification.
 */
import {
  compareScheduledEvents,
  GuildScheduledEventStatus,
  isUnfinishedEvent,
  isVoiceChannelType,
  ScheduledEventRoutes,
  type CreateGuildScheduledEventRequest,
  type GatewayDispatch,
  type GuildScheduledEvent,
  type GuildScheduledEventUser,
  type ModifyGuildScheduledEventRequest,
  type Snowflake,
} from '@neosocial/protocol';
import { create } from 'zustand';
import { tr } from '../i18n';
import { notify } from '../notify/notify';
import { api } from '../lib/api';
import { flagOn, onConfigChange } from '../store/config';
import { useData } from '../store/data';
import { activeGuildId } from '../store/selectors';
import { toast } from '../ui/overlay';

export interface EventsState {
  /** Unfinished events by id. */
  byId: Record<Snowflake, GuildScheduledEvent>;
  /** Events you are interested in. */
  mine: Record<Snowflake, true>;
  /** Guilds whose list has been fetched since READY. */
  loaded: Record<Snowflake, true>;
}

export const emptyEvents = (): EventsState => ({ byId: {}, mine: {}, loaded: {} });

export const useEvents = create<EventsState>(() => emptyEvents());

/* --------------------------------------------------------------- reducer */

function put(s: EventsState, e: GuildScheduledEvent): EventsState {
  if (!isUnfinishedEvent(e)) return drop(s, e.id);
  const old = s.byId[e.id];
  const next: GuildScheduledEvent = { ...old, ...e };
  // the gateway's copy has no `nx_user_interested`: what you are is in `mine`
  delete next.nx_user_interested;
  const mine =
    e.nx_user_interested === undefined
      ? s.mine
      : e.nx_user_interested
        ? { ...s.mine, [e.id]: true as const }
        : without(s.mine, e.id);
  return { ...s, byId: { ...s.byId, [e.id]: next }, mine };
}

function without<T>(r: Record<string, T>, id: string): Record<string, T> {
  if (!(id in r)) return r;
  const out = { ...r };
  delete out[id];
  return out;
}

function drop(s: EventsState, id: Snowflake): EventsState {
  if (!(id in s.byId) && !(id in s.mine)) return s;
  return { ...s, byId: without(s.byId, id), mine: without(s.mine, id) };
}

/** One gateway dispatch applied to the events (pure; `me` is your user id). */
export function applyEventFrame(
  s: EventsState,
  frame: GatewayDispatch,
  me: Snowflake | null,
): EventsState {
  switch (frame.t) {
    case 'READY':
      return emptyEvents();
    case 'GUILD_SCHEDULED_EVENT_CREATE':
    case 'GUILD_SCHEDULED_EVENT_UPDATE':
      return put(s, frame.d);
    case 'GUILD_SCHEDULED_EVENT_DELETE':
      return drop(s, frame.d.id);
    case 'GUILD_SCHEDULED_EVENT_USER_ADD':
    case 'GUILD_SCHEDULED_EVENT_USER_REMOVE': {
      const add = frame.t === 'GUILD_SCHEDULED_EVENT_USER_ADD';
      const id = frame.d.guild_scheduled_event_id;
      const e = s.byId[id];
      const mineNow = frame.d.user_id === me;
      // your own toggle was counted already when the request answered
      if (mineNow && !!s.mine[id] === add) return s;
      const byId = e
        ? {
            ...s.byId,
            [id]: { ...e, user_count: Math.max(0, (e.user_count ?? 0) + (add ? 1 : -1)) },
          }
        : s.byId;
      const mine = mineNow
        ? add
          ? { ...s.mine, [id]: true as const }
          : without(s.mine, id)
        : s.mine;
      return { ...s, byId, mine };
    }
    case 'NX_GUILD_SCHEDULED_EVENT_START':
      return put(s, frame.d.event);
    default:
      return s;
  }
}

/** Replaces one guild's events with a fresh list (its REST answer). */
export function applyEventList(
  s: EventsState,
  guildId: Snowflake,
  list: readonly GuildScheduledEvent[],
): EventsState {
  const byId: Record<Snowflake, GuildScheduledEvent> = {};
  for (const [id, e] of Object.entries(s.byId)) if (e.guild_id !== guildId) byId[id] = e;
  const mine: Record<Snowflake, true> = {};
  for (const id of Object.keys(s.mine)) if (byId[id]) mine[id] = true;
  let out: EventsState = { byId, mine, loaded: { ...s.loaded, [guildId]: true } };
  for (const e of list) out = put(out, e);
  return out;
}

/* ------------------------------------------------------------- selectors */

/** A guild's unfinished events in Discord's order: happening now first, then by start. */
export function eventsOf(s: EventsState, guildId: Snowflake | null): GuildScheduledEvent[] {
  if (!guildId) return [];
  return Object.values(s.byId)
    .filter((e) => e.guild_id === guildId)
    .sort(compareScheduledEvents);
}

/** The event happening now in a channel, if any (the earliest started). */
export function liveEventIn(s: EventsState, channelId: Snowflake): GuildScheduledEvent | null {
  let best: GuildScheduledEvent | null = null;
  for (const e of Object.values(s.byId))
    if (
      e.channel_id === channelId &&
      e.status === GuildScheduledEventStatus.ACTIVE &&
      (!best || compareScheduledEvents(e, best) < 0)
    )
      best = e;
  return best;
}

/** Events happening now in a guild. */
export function liveEventsOf(s: EventsState, guildId: Snowflake | null): GuildScheduledEvent[] {
  return eventsOf(s, guildId).filter((e) => e.status === GuildScheduledEventStatus.ACTIVE);
}

/* ------------------------------------------------------------------- API */

const R = ScheduledEventRoutes;

export const eventsApi = {
  list: (g: Snowflake) =>
    api.client.get<GuildScheduledEvent[]>(R.guildScheduledEvents(g), {
      query: { with_user_count: true },
    }),
  create: (g: Snowflake, b: CreateGuildScheduledEventRequest) =>
    api.client.post<GuildScheduledEvent>(R.guildScheduledEvents(g), b),
  modify: (g: Snowflake, e: Snowflake, b: ModifyGuildScheduledEventRequest) =>
    api.client.patch<GuildScheduledEvent>(R.guildScheduledEvent(g, e), b),
  remove: (g: Snowflake, e: Snowflake) => api.client.delete<void>(R.guildScheduledEvent(g, e)),
  users: (g: Snowflake, e: Snowflake, after?: Snowflake) =>
    api.client.get<GuildScheduledEventUser[]>(R.guildScheduledEventUsers(g, e), {
      query: { limit: 100, with_member: true, after },
    }),
  interested: (g: Snowflake, e: Snowflake, on: boolean) =>
    on
      ? api.client.put<void>(R.guildScheduledEventMe(g, e))
      : api.client.delete<void>(R.guildScheduledEventMe(g, e)),
};

/* --------------------------------------------------------------- actions */

/** The fetch in flight, per server (Console v2: another server's list may load meanwhile). */
const loading = new Map<Snowflake, Promise<void>>();

/** Fetches the server's events (once per READY, again when channels come or go). */
export function loadEvents(
  guildId: Snowflake | null = activeGuildId(useData.getState()),
): Promise<void> {
  if (!guildId || !flagOn('scheduled_events')) return Promise.resolve();
  const busy = loading.get(guildId);
  if (busy) return busy;
  const p = eventsApi
    .list(guildId)
    .then((list) => useEvents.setState((s) => applyEventList(s, guildId, list)))
    .catch(() => {
      /* the list stays as it was; the next READY tries again */
    })
    .finally(() => {
      loading.delete(guildId);
    });
  loading.set(guildId, p);
  return p;
}

// the console turned events back on while you were here: fetch them now
onConfigChange((next, prev) => {
  if (prev && !flagOn('scheduled_events', prev) && flagOn('scheduled_events', next))
    void loadEvents();
});

let reloadTimer: ReturnType<typeof setTimeout> | null = null;
function reloadSoon(): void {
  if (reloadTimer) clearTimeout(reloadTimer);
  reloadTimer = setTimeout(() => {
    reloadTimer = null;
    void loadEvents();
  }, 400);
}

/** You are interested (or not): at once here, then the server; undone if it refuses. */
export async function setInterested(e: GuildScheduledEvent, on: boolean): Promise<void> {
  const flip = (to: boolean) =>
    useEvents.setState((s) => {
      const cur = s.byId[e.id];
      if (!cur || !!s.mine[e.id] === to) return s;
      return {
        ...s,
        byId: {
          ...s.byId,
          [e.id]: { ...cur, user_count: Math.max(0, (cur.user_count ?? 0) + (to ? 1 : -1)) },
        },
        mine: to ? { ...s.mine, [e.id]: true } : without(s.mine, e.id),
      };
    });
  flip(on);
  try {
    await eventsApi.interested(e.guild_id, e.id, on);
  } catch (err) {
    flip(!on);
    throw err;
  }
}

/** Stores an event the server answered with (create, edit, start, end). */
export function rememberEvent(e: GuildScheduledEvent): void {
  useEvents.setState((s) => put(s, e));
}

export function forgetEvent(id: Snowflake): void {
  useEvents.setState((s) => drop(s, id));
}

/* --------------------------------------------------------- the gateway */

type Notice = (e: GuildScheduledEvent) => void;
let onStart: Notice | null = null;

/** The start notice's view (events/Host.tsx sets it: it knows how to join and open). */
export function setStartNotice(fn: Notice | null): void {
  onStart = fn;
}

/**
 * The OS notification for a start, through notify() (notify/notify.ts): only
 * while the window is in the background (the start notice shows in the app
 * otherwise), never on Do Not Disturb; a click opens the event's voice or
 * stage channel when it has one.
 */
function osNotify(e: GuildScheduledEvent): void {
  notify({
    kind: 'event',
    title: tr().t('ev.notice.os'),
    body: e.name,
    route: e.channel_id ? { to: 'voice', channelId: e.channel_id } : null,
    tag: 'event:' + e.id,
    // no sound, as before: a start is a notice, not a message
    silent: true,
  });
}

/** Everything the events feature does with a gateway dispatch. */
export function feedEvents(frame: GatewayDispatch): void {
  const me = useData.getState().me?.id ?? null;
  const before = useEvents.getState();
  const next = applyEventFrame(before, frame, me);
  if (next !== before) useEvents.setState(next, true);
  switch (frame.t) {
    case 'READY':
      // after the data store has READY (activeGuildId)
      queueMicrotask(() => void loadEvents());
      break;
    case 'CHANNEL_CREATE':
    case 'CHANNEL_DELETE':
      if (frame.d.guild_id && isVoiceChannelType(frame.d.type)) reloadSoon();
      break;
    case 'NX_GUILD_SCHEDULED_EVENT_START': {
      if (!flagOn('scheduled_events')) break;
      const e = frame.d.event;
      if (onStart) onStart(e);
      else toast(tr().t('ev.notice', { name: e.name }), { icon: 'calendar', tone: 'mode' });
      osNotify(e);
      break;
    }
    default:
      break;
  }
}
